import type { AdProviderTypes } from '@/types'
import type { AvailablePGEvents, LoggerEvent, StartRumEvent } from '@/types/logger.types'
import { isIframe, timer } from '@/utils/helpers'
import { isElementViewable } from '@/utils/is-element-viewable'

import type { AdLoader } from './ad-loader'
import type { AdProvider } from './ad-provider'
import { ConsentManager } from './consent-manager'
import { incrementDailyAdCall } from './daily-ad-call-counter'
import { HeaderBidding } from './header-bidding'
import type { ActiveCmps, HbAdUnitDefinition, HeaderBiddingOptions, PendingHbAuction } from './header-bidding/types'
import { OutOfPageAd } from './out-of-page-ad'
import { PageAd } from './page-ad'
import type { ProviderOutOfPageAd } from './providers/shared/provider-out-of-page-ad'
import type { ProviderPageAd } from './providers/shared/provider-page-ad'
import {
    type AdProviderRequest,
    AdProviderStatus,
    type AdTargeting,
    type AdType,
    AdPlacementType,
    type OutOfPageAdType,
    type PageAdOptions,
} from './types'
import { AdConsoleLogger } from './utils/ad-console-logger'
import { createRumTracker } from './utils/create-rum-tracker'
import { generateAdElementId } from './utils/generate-ad-id'
import { mergeTargeting } from './utils/merge-targeting'

export type ProviderKey = AdType | 'default'

export type FailedAdProvider = {
    readonly failed: true
    readonly type: AdProviderTypes
    readonly errorMessage: string
}

export type LoadingProvider = {
    readonly loading: true
    readonly type: AdProviderTypes
    readonly timeout: number
}

export type ProviderConfig = {
    type: AdProviderTypes
    timeout: number
}

export type AdServiceOptions = {
    consentAccount: string
    getCountryCode?: () => Promise<string | undefined>
    targeting: AdTargeting
    logEvent: (event: LoggerEvent) => void
    registerPageHideFallbackEvent: (event: Omit<LoggerEvent, 'dimension1'>) => () => void
    startRumEvent: StartRumEvent
    headerBiddingConfig?: HeaderBiddingOptions
    auid: string
    providersSettings: Partial<Record<ProviderKey, ProviderConfig[]>> | ProviderConfig[]
}

const SERVICE_INITIALIZATION_TIMEOUT = 10000

function isActiveProvider(provider: AdProvider | FailedAdProvider | LoadingProvider): provider is AdProvider {
    return !('loading' in provider) && !('failed' in provider)
}

export class AdService {
    private readonly options: AdServiceOptions

    private readonly logEvent: (event: LoggerEvent) => void

    private readonly startRumEvent: StartRumEvent

    private providers: Partial<Record<ProviderKey, (AdProvider | FailedAdProvider | LoadingProvider)[]>> = {}

    private currentTargeting: AdTargeting = {}

    private headerBidding: HeaderBidding | undefined = undefined

    private depsReady = false
    private serviceReady = false

    serviceReadyPromise: Promise<AdProviderStatus>
    private serviceReadyResolve: (status: AdProviderStatus) => void

    /** Times the one-time wait every early ad request sits through: construction → service ready. */
    private serviceReadyTracker: ReturnType<typeof createRumTracker>

    pageBanners: Set<PageAd> = new Set()

    private readonly refreshTimers: Map<PageAd, number> = new Map()

    outOfPageBanners: Set<OutOfPageAd> = new Set()

    consentManager: ConsentManager

    adConsoleLogger = new AdConsoleLogger('service')

    constructor(options: AdServiceOptions) {
        this.adConsoleLogger.debug('create service', { options })
        this.options = options
        this.consentManager = new ConsentManager({
            handleConsentlessEnv: async () => {
                const countryCode = await this.options.getCountryCode?.()
                return countryCode === 'US'
            },
            consentAccount: this.options.consentAccount,
            logEvent: this.options.logEvent,
        })
        this.logEvent = options.logEvent
        this.startRumEvent = options.startRumEvent
        const providersSettings = Array.isArray(options.providersSettings)
            ? { default: options.providersSettings }
            : options.providersSettings

        for (const [type, providers] of Object.entries(providersSettings)) {
            this.providers[type as ProviderKey] = providers.map(({ type: providerType, timeout }) => ({
                loading: true,
                type: providerType,
                timeout,
            }))
        }
        this.currentTargeting = options.targeting

        const { promise, resolve } = Promise.withResolvers<AdProviderStatus>()
        this.serviceReadyPromise = promise
        this.serviceReadyResolve = resolve

        this.serviceReadyTracker = createRumTracker('ad-service-ready', this.startRumEvent)

        timer(SERVICE_INITIALIZATION_TIMEOUT).then(() => this.markServiceReady('timeout'))
        this.initDeps()
    }

    setProvider(index: number, provider: AdProvider | FailedAdProvider, type: ProviderKey = 'default') {
        const arr = this.providers[type]
        if (arr && index < arr.length) {
            arr[index] = provider
        }

        if ('failed' in provider) {
            this.adConsoleLogger.debug(`provider failed`, { type, index, error: provider.errorMessage })
        } else {
            this.adConsoleLogger.debug(`provider ready`, { type, index, provider: provider.type })
            this.setProviderTargeting(provider, this.currentTargeting)
        }
        this.checkServiceReady()
    }

    private async initDeps() {
        const tracker = createRumTracker('ad-service-deps-init', this.startRumEvent)
        let headerBiddingResult: { error?: string; success: boolean } | undefined

        if (isIframe()) {
            const activeCmps = await this.consentManager.install()
            headerBiddingResult = await this.initHeaderBidding(activeCmps)
        } else {
            ;[, headerBiddingResult] = await Promise.all([
                this.consentManager.install(),
                this.initHeaderBidding({ gpp: true, tcf: true }),
            ])
        }
        tracker.send({
            status: headerBiddingResult?.success ? 'success' : 'error',
            dimension1: headerBiddingResult?.error,
        })
        this.depsReady = true
        this.checkServiceReady()
    }

    private async initHeaderBidding(activeCmps: ActiveCmps): Promise<{ error?: string; success: boolean }> {
        if (!this.options.headerBiddingConfig) {
            return { success: true }
        }

        this.headerBidding = new HeaderBidding(this.options.headerBiddingConfig, activeCmps, this.startRumEvent)
        await this.headerBidding.ready
        this.headerBidding.updateTargeting(this.currentTargeting)

        return this.headerBidding.getUserSignals()
    }

    private checkServiceReady() {
        if (this.serviceReady) {
            return
        }

        const allTypesReady = Object.values(this.providers).every((slots) => this.isTypeReady(slots))

        if (allTypesReady && this.depsReady) {
            this.markServiceReady()
        }
    }

    private markServiceReady(reason?: string) {
        if (!this.serviceReady) {
            const readiness = reason === 'timeout' ? this.getReadinessSnapshot() : undefined

            if (readiness) {
                this.adConsoleLogger.error('service ready via timeout fallback - still pending:', readiness)
            }

            this.serviceReadyTracker.send({
                status: reason === 'timeout' ? 'error' : 'success',
                dimension1: readiness,
            })
            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_service_ready',
                dimension3: readiness,
            })

            this.serviceReady = true
            this.adConsoleLogger.debug(reason ? `service ready (${reason})` : 'service ready')
            this.serviceReadyResolve(AdProviderStatus.online)
        }
    }

    private isTypeReady(slots: (AdProvider | FailedAdProvider | LoadingProvider)[]): boolean {
        return (
            slots.some((p): p is AdProvider => !('loading' in p) && !('failed' in p) && !!p.blocksServiceReady) ||
            slots.every((p) => !('loading' in p))
        )
    }

    /**
     * Snapshot of why the natural (non-timeout) readiness check hasn't fired yet: whether deps are
     * still pending, and which provider types still have loading slots without a blocksServiceReady
     * provider. Only meaningful when logged right as the SERVICE_INITIALIZATION_TIMEOUT fallback fires.
     */
    private getReadinessSnapshot(): string {
        const pendingTypes = Object.entries(this.providers)
            .filter(([, slots]) => !!slots && !this.isTypeReady(slots))
            .map(([type, slots]) => `${type}:${slots?.filter((p) => 'loading' in p).length}/${slots?.length}loading`)

        return `depsReady=${this.depsReady};pendingTypes=${pendingTypes.join(',') || 'none'}`
    }

    private getProvidersByType(type: ProviderKey): Array<AdProvider | FailedAdProvider | LoadingProvider> {
        return this.providers[type] || this.providers.default || []
    }

    private async requestAd<T extends ProviderPageAd | ProviderOutOfPageAd>(
        providerMethod: (provider: AdProvider, requestOptions: AdProviderRequest) => T,
        publicBanner: {
            setIntermediateInternalBanner: (internalBanner: T) => void
            setInternalBanner: (internalBanner: T) => void
            triggerEmpty: () => void
        },
        {
            placement,
            adSource,
            getHbAdUnit,
        }: {
            placement: AdType
            adSource?: string
            /** Enables the auction pipeline: returns the ad unit a provider would auction for this placement. */
            getHbAdUnit?: (provider: AdProvider, requestOptions: AdProviderRequest) => HbAdUnitDefinition | undefined
        },
    ): Promise<boolean> {
        const requestTracker = createRumTracker('request-ad', this.startRumEvent)
        const requestContext = { targeting: { ad_placement: placement } } as const
        const providerSlots = this.getProvidersByType(placement)
        this.adConsoleLogger.debug(`waterfall start`, { placement, providers: providerSlots.length })

        const pendingAuctions = new Map<AdProvider, PendingHbAuction>()
        const getAuctionContext = (provider: AdProvider) => ({
            placement,
            provider: provider.type,
            waterfallIndex: providerSlots.indexOf(provider),
            requestContext,
        })
        const ensurePendingAuction = (fromIndex: number) => {
            if (!this.headerBidding || !getHbAdUnit) {
                return
            }

            const provider = providerSlots.slice(fromIndex).find(isActiveProvider)
            if (!provider || pendingAuctions.has(provider)) {
                return
            }

            try {
                const requestOptions = { headerBidding: this.headerBidding, requestContext }
                const pending = this.headerBidding.getOrRunAuction(
                    () => getHbAdUnit(provider, requestOptions),
                    getAuctionContext(provider),
                )
                if (pending) {
                    // the waterfall may fill before this step — don't reject unhandled
                    pending.result.catch(() => {})
                    pendingAuctions.set(provider, pending)
                }
            } catch {
                // a broken ad unit config (getAdUnitByType throws) surfaces at the step's own attempt
            }
        }

        const releaseUnusedAuctions = () => {
            for (const [provider, pending] of pendingAuctions) {
                this.headerBidding?.releaseAuction(pending, getAuctionContext(provider))
            }
        }

        for (let i = 0; i < providerSlots.length; i++) {
            ensurePendingAuction(i)
            ensurePendingAuction(i + 1)

            const stepTracker = createRumTracker('request-ad-step', this.startRumEvent)

            const orderAdValue = i + 1
            const currentProvider = providerSlots[i]
            const cancelPageHiddenTracking = this.options.registerPageHideFallbackEvent({
                event: 'custom_event',
                eventName: 'ad_request_attempt_failed',
                adPlacement: placement,
                adProvider: currentProvider?.type,
                waterfallIndex: orderAdValue,
                ...(adSource && { dimension2: adSource }),
                dimension3: 'page_hide_fallback',
            })

            this.adConsoleLogger.debug(`waterfall attempt [${orderAdValue}/${providerSlots.length}]`, {
                placement,
                provider: currentProvider?.type || 'unknown',
            })

            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_waterfall_attempt',
                adPlacement: placement,
                adProvider: currentProvider?.type,
                waterfallIndex: orderAdValue,
                ...(adSource && { dimension2: adSource }),
                dimension3: `${providerSlots.length}`,
            })

            if (!currentProvider || 'failed' in currentProvider || 'loading' in currentProvider) {
                let logErrorMessage = 'Provider is not initialized: unexpected error'

                if (currentProvider && 'failed' in currentProvider) {
                    logErrorMessage = `Failed to load provider: ${currentProvider.errorMessage}`
                }

                if (currentProvider && 'loading' in currentProvider) {
                    logErrorMessage = 'Provider is not initialized'
                }

                stepTracker.send({
                    status: 'error',
                    dimension2: logErrorMessage,
                    dimension4: placement,
                    dimension3: `${orderAdValue}`,
                })
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_request_attempt_failed',
                    adPlacement: placement,
                    waterfallIndex: orderAdValue,
                    errorMessage: logErrorMessage,
                    adProvider: currentProvider?.type,
                    dimension3: logErrorMessage,
                    ...(adSource && { dimension2: adSource }),
                })
                cancelPageHiddenTracking()

                continue
            }

            let internalBanner: T | undefined

            const log = (
                eventName: AvailablePGEvents,
                rest?: Partial<Omit<LoggerEvent, 'dimension1' | 'dimension2'>>,
            ) => {
                this.logEvent({
                    event: 'custom_event',
                    eventName,
                    adPlacement: placement,
                    adUnit: internalBanner?.adUnit,
                    adHbCpmUsd: internalBanner?.headerBiddingAuctionResult?.renderedBid?.cpm ?? undefined,
                    adHbBidder: internalBanner?.headerBiddingAuctionResult?.renderedBid?.bidder,
                    adHbRequestId: internalBanner?.headerBiddingAuctionResult?.renderedBid?.requestId || undefined,
                    adProvider: currentProvider.type,
                    instreamDuration:
                        internalBanner?.type === AdPlacementType.OUT_OF_PAGE
                            ? internalBanner?.instreamDuration
                            : undefined,
                    waterfallIndex: orderAdValue,
                    dimension1: internalBanner?.headerBiddingAuctionResult?.winningBid?.mediaType,
                    dimension2: adSource,
                    ...rest,
                })
            }

            let requestAcknowledged = false

            try {
                const adUnit = currentProvider.getAdUnitByType(placement) || placement

                currentProvider.updateTargeting({
                    daily_call: `${incrementDailyAdCall(adUnit)}`,
                })

                internalBanner = providerMethod(currentProvider, {
                    headerBidding: this.headerBidding,
                    pendingAuction: pendingAuctions.get(currentProvider),
                    requestContext,
                })

                pendingAuctions.delete(currentProvider)

                publicBanner.setIntermediateInternalBanner(internalBanner)
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : `Cannot show the ad ${placement}`
                stepTracker.send({
                    status: 'error',
                    dimension1: currentProvider.type,
                    dimension2: errorMessage,
                    dimension4: placement,
                    dimension3: `${orderAdValue}`,
                })
                log('ad_request_unexpected_error', {
                    errorMessage,
                    dimension3: errorMessage,
                })
                cancelPageHiddenTracking()
                continue
            }

            internalBanner.addEventListener('request', () => {
                requestAcknowledged = true
                log('ad_request')
            })

            internalBanner.addEventListener('unexpected_behavior', (e) => {
                log('ad_unexpected_behavior', { dimension3: e.detail?.reason })
            })

            internalBanner.addEventListener('viewable', () => {
                this.headerBidding?.markAuctionResultUsed(internalBanner.headerBiddingAuctionResult)
                if (i === 0) {
                    log('ad_show_high_value')
                }
                stepTracker.send({
                    status: 'success',
                    dimension4: placement,
                    dimension1: currentProvider.type,
                    dimension2: internalBanner.adUnit,
                    dimension3: `${orderAdValue}`,
                })
                requestTracker.send({
                    status: 'success',
                    dimension4: placement,
                    dimension3: `${orderAdValue}`,
                })
                log('ad_show')
            })

            if (internalBanner.type === AdPlacementType.IN_PAGE) {
                internalBanner.addEventListener('showing', () => {
                    log('ad_rendered')
                })
            }

            const succeeded = await new Promise<boolean>((resolve) => {
                internalBanner.addEventListener(
                    internalBanner.type === AdPlacementType.OUT_OF_PAGE ? 'ready' : 'showing',
                    () => {
                        resolve(true)
                    },
                    { once: true },
                )
                internalBanner.addEventListener(
                    'validation_error',
                    (e) => {
                        requestAcknowledged = true
                        log('ad_request_attempt_failed', { dimension3: e.detail?.reason })
                        resolve(false)
                    },
                    { once: true },
                )
                internalBanner.addEventListener(
                    'empty',
                    (e) => {
                        if (!requestAcknowledged) {
                            log('ad_unexpected_behavior', {
                                dimension3: `Empty without request with reason: ${e.detail?.reason}`,
                            })
                        }
                        log('ad_empty', { dimension3: e.detail?.reason })
                        stepTracker.send({
                            status: 'empty',
                            dimension4: placement,
                            dimension1: currentProvider.type,
                            dimension2: internalBanner.adUnit,
                            dimension3: `${orderAdValue}`,
                        })
                        resolve(false)
                    },
                    { once: true },
                )
            })

            cancelPageHiddenTracking()

            if (succeeded) {
                releaseUnusedAuctions()
                publicBanner.setInternalBanner(internalBanner)
                return true
            }
        }
        releaseUnusedAuctions()
        this.adConsoleLogger.debug(`waterfall exhausted, no fill`, { placement, attempts: providerSlots.length })

        requestTracker.send({
            status: 'empty',
            dimension4: placement,
        })

        this.logEvent({
            event: 'custom_event',
            eventName: 'ad_request_failed',
            waterfallIndex: providerSlots.length,
            adProvider: 'unknown',
            adPlacement: placement,
            ...(adSource && { dimension2: adSource }),
        })

        publicBanner.triggerEmpty()
        return false
    }

    requestOutOfPageAd(
        placement: OutOfPageAdType,
        { loader, adSource, adRootElementId }: { loader?: AdLoader; adSource?: string; adRootElementId?: string } = {},
    ): OutOfPageAd {
        const publicBanner = new OutOfPageAd({
            placement,
            onDestroy: () => {
                this.outOfPageBanners.delete(publicBanner)
            },
            loader,
            timeout: this.getAdMaxTimeout(placement),
        })

        this.outOfPageBanners.add(publicBanner)
        publicBanner.addEventListener(
            'show_not_called_after_ready',
            () => {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_show_not_called_after_ready',
                    adPlacement: placement,
                    adUnit: publicBanner.adUnit,
                    adProvider: publicBanner.provider,
                    dimension2: adSource,
                })
            },
            { once: true },
        )
        this.adConsoleLogger.debug(`ad requested`, { placement, adSource })

        this.yieldToListeners().then(async () => {
            await this.serviceReadyPromise
            const succeeded = await this.requestAd<ProviderOutOfPageAd>(
                (provider, requestOptions) =>
                    provider.prepareOutOfPageAd(placement, { ...requestOptions, adRootElementId }),
                publicBanner,
                {
                    placement,
                    adSource,
                    getHbAdUnit: (provider, requestOptions) =>
                        provider.getOutOfPageHbAdUnit?.(placement, { ...requestOptions, adRootElementId }),
                },
            )
            if (succeeded) {
                publicBanner.triggerReady()
            }
        })

        return publicBanner
    }

    requestPageAd(options: PageAdOptions): PageAd {
        const publicBanner: PageAd = new PageAd({
            placement: options.placement,
            onDestroy: () => {
                this.pageBanners.delete(publicBanner)
                window.clearTimeout(this.refreshTimers.get(publicBanner))
                this.refreshTimers.delete(publicBanner)
            },
            onRefresh: () => {
                this.loadPageAd(publicBanner, options)
            },
        })

        this.pageBanners.add(publicBanner)
        this.adConsoleLogger.debug(`ad requested`, { placement: options.placement })

        this.yieldToListeners().then(async () => {
            await this.loadPageAd(publicBanner, options)

            if (options.refresh) {
                this.scheduleRefresh(publicBanner, options.refresh)
            }
        })

        return publicBanner
    }

    private async loadPageAd(publicBanner: PageAd, options: PageAdOptions): Promise<void> {
        await this.serviceReadyPromise

        // Banner may have been destroyed while awaiting serviceReadyPromise (e.g. ad-free mode activated)
        if (!this.pageBanners.has(publicBanner)) {
            return
        }

        const container = this.createAdContainer(options.el)

        if (!container) {
            publicBanner.triggerEmpty('no_container')
            return
        }

        const succeeded = await this.requestAd<ProviderPageAd>(
            (provider, requestOptions) => provider.requestPageAd({ ...options, el: container.id }, requestOptions),
            {
                setInternalBanner: (internalBanner) => publicBanner.setInternalBanner(internalBanner, container),
                setIntermediateInternalBanner: (internalBanner) =>
                    publicBanner.setIntermediateInternalBanner(internalBanner),
                triggerEmpty: () => publicBanner.triggerEmpty(),
            },
            { placement: options.placement },
        )

        if (succeeded) {
            publicBanner.triggerRendered()
        } else {
            container.remove()
        }
    }

    /** Defer past the microtask queue so callers can attach listeners before any provider event fires. */
    private yieldToListeners(): Promise<void> {
        return timer(10)
    }

    private createAdContainer(slotId: string): HTMLElement | null {
        const slot = document.getElementById(slotId)
        if (!slot) {
            return null
        }
        const container = document.createElement('div')
        container.id = generateAdElementId()
        slot.appendChild(container)
        return container
    }

    private scheduleRefresh(publicBanner: PageAd, refreshInterval: number): void {
        const timerId = window.setTimeout(() => {
            const isViewable = publicBanner.containerElId && isElementViewable(publicBanner.containerElId)
            if (isViewable) {
                publicBanner.refresh()
            }
            this.scheduleRefresh(publicBanner, refreshInterval)
        }, refreshInterval * 1000)
        this.refreshTimers.set(publicBanner, timerId)
    }

    setOutOfPageAdPlaybackState(play: boolean): void {
        for (const publicBanner of this.outOfPageBanners) {
            publicBanner.setPlaybackState(play)
        }
    }

    shutdown() {
        for (const publicBanner of this.pageBanners) {
            publicBanner.destroy()
        }
        for (const publicBanner of this.outOfPageBanners) {
            publicBanner.gracefullyDestroy('ad_free_mode_enabled')
        }
    }

    getTargeting(): AdTargeting {
        return structuredClone(this.currentTargeting)
    }

    updateTargeting(targeting: AdTargeting) {
        mergeTargeting(this.currentTargeting, targeting)

        this.headerBidding?.updateTargeting(this.currentTargeting)

        for (const key in this.providers) {
            this.getProvidersByType(key as ProviderKey)
                .filter(isActiveProvider)
                .forEach((provider) => {
                    this.setProviderTargeting(provider, this.currentTargeting)
                })
        }
    }

    resetHeaderBiddingCache(): void {
        this.headerBidding?.resetAuctionCache()
    }

    private setProviderTargeting(provider: AdProvider, targeting: AdTargeting) {
        try {
            provider.updateTargeting(targeting)
        } catch {
            // provider threw an error, nothing we can do
        }
    }

    getAdMaxTimeout(placement?: AdType): number {
        return this.getProvidersByType(placement || 'default')
            .filter((p): p is AdProvider | LoadingProvider => !('failed' in p))
            .reduce((accumulator, provider) => accumulator + provider.timeout, 0)
    }
}
